import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileText, Telescope } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChartWheel from "../components/ChartWheel";
import ChartPlacements from "../components/ChartPlacements";
import BirthDataForm from "../components/BirthDataForm";
import TransitsPanel from "../components/TransitsPanel";
import DocumentsPanel from "../components/DocumentsPanel";
import { SIGN_EN_ES } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useContent } from "../lib/content";

function PanelBlock({ overline, title, children, testid }) {
  return (
    <div data-testid={testid} className="border-t border-white/5 pt-12 mt-12 scroll-mt-24">
      <p className="overline mb-2">{overline}</p>
      <h2 className="font-display text-3xl sm:text-4xl text-white font-light tracking-tight mb-8">{title}</h2>
      {children}
    </div>
  );
}

export default function MiPanel() {
  const { user, setUser, signOut } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const leaving = useRef(false);

  useEffect(() => { if (user === false && !leaving.current) navigate("/ingresar", { replace: true }); }, [user, navigate]);
  if (!user) return <div className="min-h-screen" data-testid="panel-loading" />;

  const chart = user.chart;
  return (
    <div data-testid="panel-page" className="min-h-screen">
      <Navbar />
      <section className="pt-32 pb-20 max-w-6xl mx-auto px-6 user-panel">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline mb-3">{t("plat_panel_overline", "Mi panel")}</p>
            <h1 data-testid="panel-greeting" className="font-display text-5xl sm:text-6xl text-white font-light tracking-tighter">
              {t("plat_panel_greeting", "Hola, {name}.").replace("{name}", user.name)}
            </h1>
            <div className="flex flex-wrap gap-3 mt-6">
              {[["panel-documents", FileText, "Mis lecturas"], ["panel-transits", Telescope, "Mis tránsitos"]].map(([id, Icon, label]) => (
                <button key={id} data-testid={`panel-jump-${id}`}
                  onClick={() => document.querySelector(`[data-testid=${id}]`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="btn-lilac inline-flex items-center gap-2 !px-6 !py-3">
                  <Icon size={17} /> {label}
                </button>
              ))}
            </div>
          </div>
          <button data-testid="panel-logout" onClick={() => { leaving.current = true; signOut(); navigate("/"); }}
            className="btn-ghost inline-flex items-center gap-2 text-sm"><LogOut size={15} /> Salir</button>
        </div>

        <PanelBlock testid="panel-birth" overline={t("plat_birth_overline")} title={t("plat_birth_title")}>
          <BirthDataForm birth={user.birth} onSaved={setUser} />
        </PanelBlock>

        {chart && (
          <PanelBlock testid="panel-chart" overline={t("plat_chart_overline")}
            title={`Ascendente en ${SIGN_EN_ES[chart.ascendant.sign]}.`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <ChartWheel chart={chart} />
              <ChartPlacements chart={chart} />
            </div>
          </PanelBlock>
        )}

        <PanelBlock testid="panel-transits" overline={t("plat_transits_overline")} title={t("plat_transits_title")}>
          {chart ? <TransitsPanel key={user.birth?.birth_date + user.birth?.birth_time + user.birth?.birth_place} />
            : <p className="text-zinc-500">Guarda tus datos natales para ver tus tránsitos.</p>}
        </PanelBlock>

        <PanelBlock testid="panel-documents" overline={t("plat_docs_overline")} title={t("plat_docs_title")}>
          <DocumentsPanel />
        </PanelBlock>
      </section>
      <Footer />
    </div>
  );
}
